import { Button, CircularProgress, Typography, Box, Paper, Chip } from '@mui/material';
import { useMemo } from 'react';
import { CustomStartButton } from '../../shared/sharedStyles';
import { useCoursesCertificatesStatusMap, handleCertificateDownload } from "./CoursesTableUtils"
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

export const CoursesListForMobile = ({
    lngsltd, userRole, data, filteredData, handleCourseClick, handleEdit,
    handleDelete, handleDetails, handleReport, buttonRef, handleStart, userid, userfname, userlname
}) => {
    const { certificateStatusMap } = useCoursesCertificatesStatusMap(data, userid)

    const rows = useMemo(() => (filteredData ? filteredData.map((d, i) => ({
        ...d,
        id: i + 1,
        courseID: d.courseID,
        courseName: d.courseName,
        description: d.description,
        completionDuration: d.completionDuration,
        groupName: d.groupName,
    })) : []), [filteredData]);

    const isCompleted = (duration) => duration && duration !== "Not Completed";

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rows.map((row) => (
                <Paper
                    key={row.courseID}
                    elevation={0}
                    sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: isCompleted(row.completionDuration) ? '#10b981' : '#e5e7eb',
                        backgroundColor: isCompleted(row.completionDuration) ? 'rgba(16, 185, 129, 0.05)' : '#fff',
                    }}
                >
                    {/* Header with Course Name and Status */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: '#1a1a1a',
                                textDecoration: userRole === 'Admin' ? 'underline' : 'none',
                                cursor: userRole === 'Admin' ? 'pointer' : 'default',
                                flex: 1,
                                pr: 1,
                            }}
                            onClick={userRole === 'Admin' ? () => handleCourseClick(row.courseID) : null}
                        >
                            {row.courseName}
                        </Typography>
                        
                        {userRole !== 'Admin' && (
                            <Chip
                                size="small"
                                icon={isCompleted(row.completionDuration) ? 
                                    <CheckCircleIcon sx={{ fontSize: 14 }} /> : 
                                    <AccessTimeIcon sx={{ fontSize: 14 }} />
                                }
                                label={isCompleted(row.completionDuration) ? lngsltd["Completed"] || "Completed" : lngsltd["Pending"] || "Pending"}
                                sx={{
                                    backgroundColor: isCompleted(row.completionDuration) ? '#10b981' : '#f59e0b',
                                    color: '#fff',
                                    fontSize: '0.7rem',
                                    height: 24,
                                    '& .MuiChip-icon': { color: '#fff' }
                                }}
                            />
                        )}
                    </Box>

                    {/* Description */}
                    <Typography
                        variant="body2"
                        sx={{ color: '#64748b', mb: 1.5 }}
                    >
                        {row.description}
                    </Typography>

                    {/* Completion Duration for Users */}
                    {userRole !== 'Admin' && row.completionDuration && (
                        <Typography 
                            variant="caption"
                            sx={{
                                display: 'block',
                                color: isCompleted(row.completionDuration) ? '#10b981' : '#64748b',
                                mb: 1.5,
                            }}
                        >
                            <strong>{lngsltd["Duration"]}:</strong> {row.completionDuration}
                        </Typography>
                    )}

                    {/* Group Name for Admin */}
                    {userRole === 'Admin' && row.groupName && (
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                display: 'inline-block',
                                backgroundColor: '#f1f5f9',
                                color: '#475569',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                mb: 1.5,
                            }}
                        >
                            {row.groupName}
                        </Typography>
                    )}

                    {/* Actions */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            flexWrap: 'wrap',
                            gap: 1,
                            pt: 1,
                            borderTop: '1px solid #f1f5f9',
                        }}
                    >
                        {userRole === 'Admin' ? (
                            <>
                                <Button variant="outlined" size="small" onClick={() => handleEdit(row.courseID)}>
                                    {lngsltd["Edit"]}
                                </Button>
                                <Button variant="outlined" color="error" size="small" onClick={() => handleDelete(row.courseID)}>
                                    {lngsltd["Delete"]}
                                </Button>
                                <Button variant="outlined" color="info" size="small" onClick={() => handleDetails(row.courseID)}>
                                    {lngsltd["Details"]}
                                </Button>
                                <Button variant="outlined" color="warning" size="small" onClick={() => handleReport(row.courseID)}>
                                    {lngsltd["Report"]}
                                </Button>
                            </>
                        ) : (
                            <>
                                <CustomStartButton
                                    ref={buttonRef}
                                    onClick={() => handleStart(row.courseID)}
                                    sx={{ 
                                        px: 2, 
                                        py: 0.75,
                                        minWidth: 'auto', 
                                        borderRadius: 1.5,
                                    }}
                                >
                                    <Typography variant="caption" fontSize="12px" fontWeight={600}>
                                        {isCompleted(row.completionDuration) ? (lngsltd["Continue"] || "Continue") : lngsltd["Start"]}
                                    </Typography>
                                </CustomStartButton>

                                {certificateStatusMap[row.courseID] && (
                                    <CustomStartButton
                                        onClick={() => handleCertificateDownload(row.courseID, userid, userfname, userlname)}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: 1.5,
                                            backgroundColor: '#10b981',
                                            '&:hover': { backgroundColor: '#059669' }
                                        }}
                                    >
                                        <Typography variant="caption" fontSize="12px" fontWeight={600}>
                                            {lngsltd["Certificate"]}
                                        </Typography>
                                        <DownloadIcon sx={{ fontSize: 16, ml: 0.5 }} />
                                    </CustomStartButton>
                                )}
                            </>
                        )}
                    </Box>
                </Paper>
            ))}
        </Box>
    );
}